import { logError } from '../../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Calendar API Module
 * Handles all calendar/scheduling-related API calls
 */

// ============================================
// EVENT TYPES API
// ============================================

/**
 * Fetch all event types for the current user
 * GET /api/event-types
 */
export async function fetchEventTypes(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.team_id) queryParams.append('team_id', params.team_id);
    if (params.active !== undefined) queryParams.append('active', params.active);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/api/event-types${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch event types failed:", response.status, errorText);
        throw new Error("Failed to fetch event types. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch a single event type by ID
 * GET /api/event-types/{event_type_id}
 */
export async function fetchEventTypeById(eventTypeId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/event-types/${encodeURIComponent(eventTypeId)}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch event type failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Event type not found.");
        }
        throw new Error("Failed to fetch event type. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Create a new event type
 * POST /api/event-types
 */
export async function createEventType(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/event-types`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            title: payload.title,
            slug: payload.slug,
            description: payload.description,
            duration: payload.duration,
            location_type: payload.locationType || payload.location_type,
            location: payload.location,
            color: payload.color || '#3B82F6',
            is_active: payload.isActive !== undefined ? payload.isActive : true,
            requires_confirmation: payload.requiresConfirmation || false,
            buffer_before: payload.bufferBefore || 0,
            buffer_after: payload.bufferAfter || 0,
            min_notice: payload.minNotice || 60,
            max_bookings_per_day: payload.maxBookingsPerDay || null,
            schedule_id: payload.scheduleId || null,
            team_id: payload.teamId || null
        })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Create event type failed:", response.status, errorText);
        throw new Error("Failed to create event type. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Update an existing event type
 * PATCH /api/event-types/{event_type_id}
 */
export async function updateEventType(eventTypeId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/event-types/${encodeURIComponent(eventTypeId)}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Update event type failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Event type not found.");
        }
        throw new Error("Failed to update event type. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Delete an event type
 * DELETE /api/event-types/{event_type_id}
 */
export async function deleteEventType(eventTypeId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/event-types/${encodeURIComponent(eventTypeId)}`, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete event type failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Event type not found.");
        }
        throw new Error("Failed to delete event type. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

// ============================================
// SCHEDULES API
// ============================================

/**
 * Fetch all schedules for the current user
 * GET /api/schedules
 */
export async function fetchSchedules() {
    const response = await fetchWithAuth(`${BASE_URL}/api/schedules`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch schedules failed:", response.status, errorText);
        throw new Error("Failed to fetch schedules. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch a single schedule by ID
 * GET /api/schedules/{schedule_id}
 */
export async function fetchScheduleById(scheduleId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/schedules/${encodeURIComponent(scheduleId)}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch schedule failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Schedule not found.");
        }
        throw new Error("Failed to fetch schedule. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Create a new schedule
 * POST /api/schedules
 */
export async function createSchedule(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/schedules`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            name: payload.name,
            timezone: payload.timezone,
            is_default: payload.isDefault || false,
            availability: payload.availability || []
        })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Create schedule failed:", response.status, errorText);
        throw new Error("Failed to create schedule. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Update an existing schedule
 * PATCH /api/schedules/{schedule_id}
 */
export async function updateSchedule(scheduleId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/schedules/${encodeURIComponent(scheduleId)}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Update schedule failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Schedule not found.");
        }
        throw new Error("Failed to update schedule. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Delete a schedule
 * DELETE /api/schedules/{schedule_id}
 */
export async function deleteSchedule(scheduleId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/schedules/${encodeURIComponent(scheduleId)}`, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete schedule failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Schedule not found.");
        }
        throw new Error("Failed to delete schedule. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Add a date override to a schedule
 * POST /api/schedules/{schedule_id}/overrides
 */
export async function addDateOverride(scheduleId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/schedules/${encodeURIComponent(scheduleId)}/overrides`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            date: payload.date,
            is_unavailable: payload.isUnavailable !== undefined ? payload.isUnavailable : true,
            start_time: payload.startTime || null,
            end_time: payload.endTime || null,
            reason: payload.reason || null
        })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Add date override failed:", response.status, errorText);
        throw new Error("Failed to add date override. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Delete a date override from a schedule
 * DELETE /api/schedules/{schedule_id}/overrides/{override_id}
 */
export async function deleteDateOverride(scheduleId, overrideId) {
    const response = await fetchWithAuth(
        `${BASE_URL}/api/schedules/${encodeURIComponent(scheduleId)}/overrides/${encodeURIComponent(overrideId)}`,
        { method: "DELETE" }
    );

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete date override failed:", response.status, errorText);
        throw new Error("Failed to delete date override. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

// ============================================
// BOOKINGS API
// ============================================

/**
 * Fetch all bookings for the current user
 * GET /api/bookings
 */
export async function fetchBookings(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.event_type_id) queryParams.append('event_type_id', params.event_type_id);
    if (params.case_id) queryParams.append('case_id', params.case_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/api/bookings${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch bookings failed:", response.status, errorText);
        throw new Error("Failed to fetch bookings. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data, meta: data.meta };
}

/**
 * Fetch a single booking by ID
 * GET /api/bookings/{booking_id}
 */
export async function fetchBookingById(bookingId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/bookings/${encodeURIComponent(bookingId)}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch booking failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Booking not found.");
        }
        throw new Error("Failed to fetch booking. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Create a new booking
 * POST /api/bookings
 */
export async function createBooking(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/bookings`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            event_type_id: payload.eventTypeId || payload.event_type_id,
            start_time: payload.startTime || payload.start_time,
            attendees: payload.attendees || [],
            case_id: payload.caseId || payload.case_id || null,
            notes: payload.notes || null,
            location: payload.location || null
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError("Create booking failed:", response.status, errorData);

        if (errorData.error?.code === 'SLOT_UNAVAILABLE') {
            throw new Error("The selected time slot is no longer available.");
        }
        throw new Error(errorData.error?.message || "Failed to create booking. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Update/reschedule a booking
 * PATCH /api/bookings/{booking_id}
 */
export async function updateBooking(bookingId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/bookings/${encodeURIComponent(bookingId)}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError("Update booking failed:", response.status, errorData);

        if (response.status === 404) {
            throw new Error("Booking not found.");
        }
        if (errorData.error?.code === 'SLOT_UNAVAILABLE') {
            throw new Error("The selected time slot is no longer available.");
        }
        throw new Error("Failed to update booking. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Cancel a booking
 * POST /api/bookings/{booking_id}/cancel
 */
export async function cancelBooking(bookingId, payload = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/api/bookings/${encodeURIComponent(bookingId)}/cancel`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            reason: payload.reason || null,
            notify_attendees: payload.notifyAttendees !== undefined ? payload.notifyAttendees : true
        })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Cancel booking failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Booking not found.");
        }
        throw new Error("Failed to cancel booking. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Confirm a pending booking
 * POST /api/bookings/{booking_id}/confirm
 */
export async function confirmBooking(bookingId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/bookings/${encodeURIComponent(bookingId)}/confirm`, {
        method: "POST",
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Confirm booking failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Booking not found.");
        }
        throw new Error("Failed to confirm booking. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

// ============================================
// SLOTS API
// ============================================

/**
 * Get available time slots for an event type
 * GET /api/slots
 */
export async function fetchAvailableSlots(params) {
    const queryParams = new URLSearchParams();

    queryParams.append('event_type_id', params.eventTypeId || params.event_type_id);
    queryParams.append('start_date', params.startDate || params.start_date);
    queryParams.append('end_date', params.endDate || params.end_date);
    if (params.timezone) queryParams.append('timezone', params.timezone);

    const url = `${BASE_URL}/api/slots?${queryParams}`;

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch available slots failed:", response.status, errorText);
        throw new Error("Failed to fetch available slots. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

// ============================================
// CALENDAR STATS API
// ============================================

/**
 * Get calendar statistics
 * GET /api/calendar/stats
 */
export async function fetchCalendarStats(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/api/calendar/stats${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch calendar stats failed:", response.status, errorText);
        throw new Error("Failed to fetch calendar statistics. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}
